import type { KeyboardEvent } from "react";
import { useCallback, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import type { JournalAccount } from "@/apis/gql/types";
import {
  AsyncComboBox,
  type AsyncComboBoxProps,
} from "@/components/ui/async-combo-box";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import type { ServerFilters } from "@/hooks/useQueryFilters";
import { useLazyGetJournalAccounts } from "@/queries/ledger/accounts/get-all";

export type JournalAccountsInputProps = {
  name: string;
  description?: string;
  label?: string;
  placeholder?: string;
  autoFocus?: boolean;
  onKeyDown?: (e: KeyboardEvent<HTMLButtonElement | HTMLInputElement>) => void;
  disabled?: boolean;
  readOnly?: boolean;
  variant?: "ghost" | "outline";
  getDisplayValue?: AsyncComboBoxProps<JournalAccount>["getDisplayValue"];
  triggerClassName?: AsyncComboBoxProps<JournalAccount>["triggerClassName"];
  className?: AsyncComboBoxProps<JournalAccount>["className"];
  formItemClassName?: React.HTMLAttributes<HTMLDivElement>["className"];
};

export const JournalAccountsInput = ({
  autoFocus,
  label,
  name,
  placeholder = "",
  description,
  className,
  onKeyDown,
  disabled,
  readOnly,
  variant,
  formItemClassName,
  ...rest
}: JournalAccountsInputProps) => {
  const { t } = useTranslation();
  const form = useFormContext();

  const { fetchJournalAccounts } = useLazyGetJournalAccounts({});

  const [hasNextPage, setHasNextPage] = useState(true);
  const currentPageRef = useRef(0);

  const fetcher = async (_query?: string | ServerFilters) => {
    // Base filter to exclude internal accounts with number starting with 0000000000
    const baseFilter: ServerFilters = {
      number: { value: "0000000000%", op: "notILike" },
    };

    const params: { query: ServerFilters; global?: string } = {
      query: { ...baseFilter },
    };

    if (typeof _query === "string") {
      params.global = _query;
    } else if (_query) {
      params.query = {
        ...params.query,
        ..._query,
      };
    }

    const { data, pagination } = await fetchJournalAccounts(params);
    currentPageRef.current = pagination?.currentPage ?? 1;
    setHasNextPage(pagination?.hasNextPage ?? false);

    return data;
  };

  const resolveValue = async (value: string) => {
    const { data } = await fetchJournalAccounts({
      query: {
        [name]: { value, op: "eq" },
      },
    });
    return data[0] ?? null;
  };

  const loadMore = useCallback(
    async (searchTerm?: string) => {
      const nextPage = currentPageRef.current + 1;

      const { data, pagination } = await fetchJournalAccounts(
        {
          query: { number: { value: "0000000000%", op: "notILike" } },
          global: searchTerm,
        },
        nextPage,
      );

      currentPageRef.current = nextPage;
      setHasNextPage(pagination?.hasNextPage ?? false);

      return data;
    },
    [fetchJournalAccounts],
  );

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => {
        return (
          <FormItem className={formItemClassName}>
            {label && <FormLabel>{label}</FormLabel>}
            <FormControl>
              <AsyncComboBox<JournalAccount>
                autoFocus={autoFocus}
                fetcher={fetcher}
                resolveValue={resolveValue}
                onFetchMore={loadMore}
                hasMore={hasNextPage}
                getOptionValue={(account) => account.journalAccountId}
                getOptionLabel={(account) => account.name ?? ""}
                getOptionSubLabel={(account) => account.number ?? ""}
                value={field.value}
                onChange={(value) => {
                  field.onChange(value);
                }}
                notFound={
                  <div className="py-2 text-center text-sm">
                    {t("no_results_found")}
                  </div>
                }
                placeholder={placeholder}
                className={className}
                onKeyDown={onKeyDown}
                disabled={disabled}
                readOnly={readOnly}
                variant={variant}
                {...rest}
              />
            </FormControl>
            {description && <FormDescription>{description}</FormDescription>}
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
};
